<template>
  <!-- 设备导入弹窗 -->
  <a-modal
    :title="title"
    :visible="open"
    :confirm-loading="uploading"
    @cancel="importExcelHandleCancel"
    @ok="importExcelHandleChange"
  >
    <a-spin tip="上传中..." :spinning="uploading">
      <!-- 文件拖拽上传区域 -->
      <a-upload-dragger
        name="file"
        accept=".xlsx, .xls"
        :file-list="fileList"
        :multiple="false"
        :remove="handleRemove"
        :before-upload="beforeUpload"
      >
        <p class="ant-upload-drag-icon">
          <a-icon type="inbox" />
        </p>
        <p class="ant-upload-text">请将文件拖拽到此处上传</p>
        <p class="ant-upload-hint">支持单个上传，也可以点击后选择文件上传</p>
      </a-upload-dragger>
      <!-- 下载模板链接 -->
      <!-- <a
        @click="importTemplate"
        v-hasPermi="['system:equipment:importTemplate']"
        >下载模板</a
      > -->
    </a-spin>
  </a-modal>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
/**
 * 设备导入Excel组件
 * 功能：提供设备数据的Excel导入功能
 * - 支持拖拽或点击上传Excel文件（.xlsx, .xls格式）
 * - 导入前检查项目选择状态
 * - 导入成功后触发ok事件通知父组件刷新数据
 */
import { importEquipmentData } from '@/api/system/equipment'
import { showImportResult, isImportSuccessCode } from '@/utils/importResult'

export default {
  name: 'EquipmentImportExcel',
  data() {
    return {
      // 弹窗标题
      title: '设备导入',
      // 弹窗显示/隐藏状态
      open: false,
      // 文件列表（只支持单个文件）
      fileList: [],
      // 上传中状态
      uploading: false,
      // 是否支持更新已存在数据（0-不支持，1-支持）
      updateSupport: 0,
    }
  },
  methods: {
    /**
     * 取消导入操作
     * 关闭弹窗、清空文件列表，并触发ok事件
     */
    importExcelHandleCancel() {
      this.open = false
      this.fileList = []
      $emit(this, 'ok')
    },

    /**
     * 打开导入弹窗
     * 检查是否选择了项目，如果未选择则提示用户
     */
    importExcelHandleOpen() {
      // 检查是否选择了项目
      const currentProjectId = this.$store.getters.currentProjectId
      if (!currentProjectId) {
        this.$message.warning('请先选择项目', 3)
        return
      }
      this.open = true
    },

    /**
     * 文件上传前的处理
     * 将选中的文件添加到文件列表，并阻止自动上传（手动控制上传时机）
     * @param {File} file - 选中的文件对象
     * @returns {Boolean} 返回false阻止自动上传
     */
    beforeUpload(file) {
      this.fileList = [file]
      return false
    },

    /**
     * 执行导入操作
     * 验证文件、检查项目选择，然后上传文件到后端进行导入
     */
    importExcelHandleChange() {
      // 验证是否选择了文件
      if (!this.fileList || this.fileList.length === 0) {
        this.$message.warning('请先选择要导入的文件', 3)
        return
      }
      // 检查是否选择了项目
      const currentProjectId = this.$store.getters.currentProjectId
      if (!currentProjectId) {
        this.$message.warning('请先选择项目', 3)
        return
      }

      // 开始上传
      this.uploading = true

      // 构建FormData，包含文件和其他参数
      const formData = new FormData()
      formData.append('file', this.fileList[0]) // 上传的文件
      formData.append('updateSupport', this.updateSupport) // 是否支持更新
      formData.append('projectId', currentProjectId) // 项目ID（必填）

      // 调用导入接口
      importEquipmentData(formData)
        .then((response) => {
          if (isImportSuccessCode(response.code)) {
            // 导入成功
            showImportResult(response)
            this.open = false
            this.fileList = []
            // 通知父组件刷新数据
            $emit(this, 'ok')
          } else {
            // 导入失败
            this.$message.error(response.message || '导入失败')
          }
        })
        .catch(() => {
          // 请求异常
          this.$message.error('导入失败，请稍后重试')
        })
        .finally(() => {
          // 无论成功或失败，都关闭上传状态
          this.uploading = false
        })
    },

    /**
     * 移除文件
     * 清空文件列表并重置上传状态
     */
    handleRemove() {
      this.fileList = []
      this.uploading = false
    },
  },
  emits: ['ok'],
}
</script>

<style scoped>
/* 弹窗按钮样式 - 所有按钮圆角设置为 4px */
/deep/ .ant-modal-footer .ant-btn {
  border-radius: 4px !important;
}

/* 确认按钮（主要按钮）样式 */
/deep/ .ant-modal-footer .ant-btn-primary {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  border-radius: 4px !important;
  color: #FFFFFF !important;
}

/* 确认按钮悬停状态 */
/deep/ .ant-modal-footer .ant-btn-primary:hover:not(:disabled) {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
  color: #FFFFFF !important;
}

/* 确认按钮聚焦状态 */
/deep/ .ant-modal-footer .ant-btn-primary:focus:not(:disabled) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
}

/* 确认按钮激活状态 */
/deep/ .ant-modal-footer .ant-btn-primary:active:not(:disabled) {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
}

/* 取消按钮（默认按钮）样式 */
/deep/ .ant-modal-footer .ant-btn-default {
  border-radius: 4px !important;
}
</style>
